import { useState, useMemo, useEffect } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { LocationType, Match, TournamentEvent } from "../../../types/types";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { Button, Stack, SvgIcon, Typography } from "@mui/material";
import {
  dbDeleteGroupMatch,
  dbMatchType,
  dbUploadGroupMatches,
  dbTournamentEvent,
  dbUploadTournamentEvents,
  dbDeleteTournamentEvent,
} from "../../../api/api";
import NotificationService from "../../../components/notification/NotificationService";
import CheckIcon from "@mui/icons-material/Check";
import useConfirm from "../../../hooks/useConfirm";
import PlusIcon from "@heroicons/react/24/solid/PlusIcon";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants";
import UndoIcon from "@mui/icons-material/Undo";
import AdminMatch from "./MatchCard";
import AdminTournamentEvent from "./TournamentEventCard";
import { updateTimes } from "../../../updateTimes";
import {
  deleteGroupMatch,
  setEditingMatch,
} from "../../../features/matches/matchesSlice";
import { deleteTournamentEvent } from "../../../features/tournamentEvents/tournamentEventsSlice";
import { calculateOverlap } from "../../../utils";
import ReorderIcon from "@mui/icons-material/Reorder";

interface Column {
  items: (Match | TournamentEvent)[];
  location: LocationType;
}

interface Props { }

const Matches = (props: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const matches = useAppSelector((state) => state.matches.groupMatches);
  const tournamentEvents = useAppSelector((state) => state.tournamentEvents.tournamentEvents);

  const sortedMatches = useMemo(
    () => [...matches].sort((a, b) => a.startTime - b.startTime),
    [matches]
  );

  const sortedTournamentEvents = useMemo(
    () => [...tournamentEvents].sort((a, b) => a.startTime - b.startTime),
    [tournamentEvents]
  );

  const tournament = useAppSelector(
    (state) => state.tournament.tournament
  );
  const gapTime = useAppSelector(
    (state) => state.settings.settings
  )?.gapTime;
  const _locations = useAppSelector((state) => state.locations.locations);

  const [overlap, setOverlap] = useState<
    { matchId1: string; matchId2: string; overlappingTeamId?: string } | undefined
  >();

  const [Dialog, confirmDelete] = useConfirm(
    "Er du sikker?",
    "Er du sikker på at du vil slette denne kamp/event?"
  );

  const [initialColumns, setInitialColumns] = useState<any>();
  const [tempColumns, setTempColumns] = useState<any>();

  // 🆕 Build columns every time matches or locations update
  useEffect(() => {
    if (!_locations.length) return;

    const newLocations = _locations.reduce(
      (acc, location) => {
        const locationMatches = sortedMatches.filter(
          (match) => match.location.id === location.id
        );
        const locationEvents = sortedTournamentEvents.filter(
          (event) => event.location.id === location.id
        );

        // Combine and sort by startTime
        const combinedItems = [...locationMatches, ...locationEvents].sort(
          (a, b) => a.startTime - b.startTime
        );

        return {
          ...acc,
          [location.id]: {
            location,
            items: combinedItems,
          },
        };
      },
      {}
    );

    setInitialColumns(newLocations);
    setTempColumns(newLocations);
  }, [sortedMatches, sortedTournamentEvents, _locations]);

  // Check for overlaps on tempColumns change
  useEffect(() => {
    checkForOverlaps();
  }, [tempColumns]);

  const matchesHasNotBeenReordered = () =>
    JSON.stringify(initialColumns) === JSON.stringify(tempColumns);

  const onDragEnd = (result: any, columns: any, setColumns: any) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId !== destination.droppableId) {
      const sourceColumn = columns[source.droppableId];
      const destColumn = columns[destination.droppableId];
      const sourceItems = [...sourceColumn.items];
      const destItems = [...destColumn.items];

      const [removed] = sourceItems.splice(source.index, 1);
      destItems.splice(destination.index, 0, removed);

      const result = {
        ...columns,
        [source.droppableId]: {
          ...sourceColumn,
          items: updateTimes(sourceItems, tournament.date, gapTime ?? 0),
        },
        [destination.droppableId]: {
          ...destColumn,
          items: updateTimes(destItems, tournament.date, gapTime ?? 0),
        },
      };
      setColumns(result);
    } else {
      const column = columns[source.droppableId];
      const copiedItems = [...column.items];
      const [removed] = copiedItems.splice(source.index, 1);
      copiedItems.splice(destination.index, 0, removed);

      const result = {
        ...columns,
        [source.droppableId]: {
          ...column,
          items: updateTimes(copiedItems, tournament.date, gapTime ?? 0),
        },
      };
      setColumns(result);
    }
  };

  const checkForOverlaps = () => {
    const newMatches = getCurrentMatches();
    const overlap = calculateOverlap(newMatches);
    setOverlap(overlap || undefined);
  };

  const getCurrentMatches = () => {
    if (!tempColumns) return [];
    const newMatches: Match[] = [];
    for (const [, column] of Object.entries(tempColumns)) {
      (column as Column).items.forEach((item) => {
        // Only push matches, not tournament events
        if ('matchType' in item) {
          newMatches.push({
            ...item,
            location: (column as Column).location,
          });
        }
      });
    }
    return newMatches;
  };

  const getCurrentTournamentEvents = () => {
    if (!tempColumns) return [];
    const newEvents: TournamentEvent[] = [];
    for (const [, column] of Object.entries(tempColumns)) {
      (column as Column).items.forEach((item) => {
        // Only push tournament events, not matches
        if (!('matchType' in item)) {
          newEvents.push({
            ...item,
            location: (column as Column).location,
            tournament: tournament
          });
        }
      });
    }
    return newEvents;
  };

  const save = () => {
    const uploadMatchData = getCurrentMatches().map(
      ({ homeTeamEvents, awayTeamEvents, referees, ...match }) => ({
        ...match,
        awayTeam: match.awayTeam?.id,
        group: match.group?.id,
        homeTeam: match.homeTeam?.id,
        location: match.location.id,
        pulje: match.pulje.id,
        tournament: match.tournament.id,
      })
    ) as dbMatchType[];

    const uploadEventData = getCurrentTournamentEvents().map(
      (event) => ({
        id: event.id,
        tournament: event.tournament.id,
        title: event.title,
        startTime: event.startTime,
        endTime: event.endTime,
        location: event.location.id,
        status: event.status,
        duration: event.duration,
      })
    ) as dbTournamentEvent[];

    const promises = [];
    if (uploadMatchData.length > 0) {
      promises.push(dbUploadGroupMatches(uploadMatchData));
    }
    if (uploadEventData.length > 0) {
      promises.push(dbUploadTournamentEvents(uploadEventData));
    }

    Promise.all(promises)
      .then(() => {
        setInitialColumns(tempColumns);
        NotificationService.showToast({
          severity: "success",
          text: "Kampprogrammet blev opdateret!",
          duration: 3000,
        });
      })
      .catch(() => {
        NotificationService.showToast({
          severity: "error",
          text: "Kampprogrammet kunne ikke opdateres.",
          duration: 3000,
        });
      });
  };

  const onDeleteMatchHandler = async (matchId: string) => {
    let locationId = "";
    for (const [key, value] of Object.entries(tempColumns)) {
      if ((value as Column).items.some((item) => item.id === matchId)) {
        locationId = key;
        break;
      }
    }

    const confirmed = await confirmDelete();
    if (confirmed) {
      dbDeleteGroupMatch(matchId).then(() => {
        dispatch(deleteGroupMatch(matchId));

        const updatedColumns = {
          ...tempColumns,
          [locationId]: {
            ...tempColumns[locationId],
            items: tempColumns[locationId].items.filter(
              (item: Match | TournamentEvent) => item.id !== matchId
            ),
          },
        };

        setTempColumns(updatedColumns);
        setInitialColumns(updatedColumns);
      });
    }
  };

  const onDeleteTournamentEventHandler = async (eventId: string) => {
    let locationId = "";
    for (const [key, value] of Object.entries(tempColumns)) {
      if ((value as Column).items.some((item) => item.id === eventId)) {
        locationId = key;
        break;
      }
    }

    const confirmed = await confirmDelete();
    if (confirmed) {
      dbDeleteTournamentEvent(eventId).then(() => {
        dispatch(deleteTournamentEvent(eventId));

        const updatedColumns = {
          ...tempColumns,
          [locationId]: {
            ...tempColumns[locationId],
            items: tempColumns[locationId].items.filter(
              (item: Match | TournamentEvent) => item.id !== eventId
            ),
          },
        };

        setTempColumns(updatedColumns);
        setInitialColumns(updatedColumns);
      });
    }
  };

  const editMatchHandler = (match: Match) => {
    dispatch(setEditingMatch(match));
    navigate(ROUTES.ADMIN_EDIT_MATCH);
  };

  const editTournamentEventHandler = (event: TournamentEvent) => {
    // TODO: Navigate to edit tournament event page or open dialog
    console.log("Edit tournament event:", event);
  };

  const getDelay = (location: LocationType) => {
    const delay = _locations.find((l) => l.id === location.id)?.delay ?? 0;
    return delay !== 0 ? `${Math.abs(delay)} min` : "";
  };

  const getDelayColor = (location: LocationType) => {
    const delay = _locations.find((l) => l.id === location.id)?.delay ?? 0;
    if (delay > 0) return "red";
    if (delay < 0) return "green";
    return "";
  };

  return (
    <>
      {Dialog()}
      <Stack
        spacing={3}
        justifyContent={sortedMatches.length > 0 ? "end" : "start"}
        height={"calc(100vh - 64px)"}
        marginLeft={"50px"}
      >
        <Stack direction="row" justifyContent="space-between" spacing={4}>
          <Typography variant="h4">Kampe</Typography>
          <div style={{ marginRight: "30px" }}>
            {matchesHasNotBeenReordered() ? (
              <>
                {sortedMatches.length === 0 && (
                  <Button
                    variant="contained"
                    startIcon={
                      <SvgIcon fontSize="small">
                        <PlusIcon />
                      </SvgIcon>
                    }
                    onClick={() => navigate(ROUTES.ADMIN_GENERATE_TOURNAMENT)}
                  >
                    Generer kampe
                  </Button>
                )}
                {sortedMatches.length > 0 && tempColumns && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={
                        <SvgIcon fontSize="small">
                          <PlusIcon />
                        </SvgIcon>
                      }
                      onClick={() => navigate(ROUTES.ADMIN_CREATE_MATCH)}
                    >
                      Opret kamp
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={
                        <SvgIcon fontSize="small">
                          <PlusIcon />
                        </SvgIcon>
                      }
                      onClick={() => navigate(ROUTES.ADMIN_CREATE_TOURNAMENT_EVENT)}
                    >
                      Opret begivenhed
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={
                        <SvgIcon fontSize="small">
                          <ReorderIcon />
                        </SvgIcon>
                      }
                      onClick={() => navigate(ROUTES.ADMIN_TABLE)}
                    >
                      Se tabel
                    </Button>
                  </Stack>
                )}
              </>
            ) : (
              <>
                <Button
                  startIcon={
                    <SvgIcon fontSize="small">
                      <CheckIcon />
                    </SvgIcon>
                  }
                  variant="contained"
                  onClick={save}
                  sx={{ marginRight: "10px" }}
                >
                  Gem ændringer
                </Button>
                <Button
                  variant="outlined"
                  startIcon={
                    <SvgIcon fontSize="small">
                      <UndoIcon />
                    </SvgIcon>
                  }
                  onClick={() => setTempColumns(initialColumns)}
                >
                  Fortryd
                </Button>
              </>
            )}
          </div>
        </Stack>

        {sortedMatches.length > 0 && tempColumns && (
          <div style={{ overflow: "scroll", height: "80vh" }}>
            <Stack direction="row">
              <DragDropContext
                onDragEnd={(result) => onDragEnd(result, tempColumns, setTempColumns)}
              >
                <div
                  style={{
                    display: "flex",
                    minHeight: "80vh",
                    justifyContent: "flex-start",
                  }}
                >
                  {Object.entries(tempColumns).map(([locationId, column]) => (
                    <Droppable key={locationId} droppableId={locationId}>
                      {(provided) => (
                        <div
                          style={{
                            minHeight: "100px",
                            display: "flex",
                            flexDirection: "column",
                            background: "white",
                            minWidth: "450px",
                            borderRadius: "15px",
                            padding: "15px",
                            marginRight: "30px",
                            boxShadow:
                              "rgba(0, 0, 0, 0.04) 0px 5px 22px, rgba(0, 0, 0, 0.03) 0px 0px 0px 0.5px",
                          }}
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <span
                              style={{
                                color: "#10957d",
                                background: "rgba(16, 149, 125, 0.15)",
                                padding: "2px 10px",
                                borderRadius: "5px",
                                alignSelf: "flex-start",
                              }}
                            >
                              {(column as Column).location.location}
                            </span>
                            <span
                              style={{
                                color: getDelayColor(
                                  (column as Column).location
                                ),
                                padding: "2px 10px",
                                borderRadius: "5px",
                                alignSelf: "flex-start",
                              }}
                            >
                              {getDelay((column as Column).location)}
                            </span>
                          </div>

                          {(column as Column).items?.map(
                            (item: Match | TournamentEvent, index: number) => {
                              // Check if item is a Match by checking for matchType property
                              if ('matchType' in item) {
                                return (
                                  <AdminMatch
                                    key={item.id}
                                    match={item}
                                    index={index}
                                    onDelete={onDeleteMatchHandler}
                                    overlap={overlap}
                                    onEdit={editMatchHandler}
                                    isEditing={!matchesHasNotBeenReordered()}
                                  />
                                );
                              } else {
                                // It's a TournamentEvent
                                return (
                                  <AdminTournamentEvent
                                    key={item.id}
                                    event={item}
                                    index={index}
                                    onDelete={onDeleteTournamentEventHandler}
                                    onEdit={editTournamentEventHandler}
                                    isEditing={!matchesHasNotBeenReordered()}
                                  />
                                );
                              }
                            }
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ))}
                </div>
              </DragDropContext>
            </Stack>
          </div>
        )}
      </Stack>
    </>
  );
};

export default Matches;
